import React, { useState } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Stack,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Monitor, MessageSquare } from 'lucide-mui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import {
  UploadState,
  FeedbackSnackbar,
  useIframeResizer,
  GEMINI_MODEL,
} from './common';
import { config } from '../../../config';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const LANGUAGES = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  ru: 'Russian',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
};

const ScreenTranslator: React.FC = () => {
  const { t } = useTranslation();
  const [uploadState, setUploadState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('en');

  useIframeResizer();

  const handleNewTranslation = () => {
    setMessages([]);
    setSelectedImage(null);
    setIsLoading(false);
    setUploadState({
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
  };

  const handleTranslate = async (imageData: string) => {
    if (!imageData || isLoading) return;

    setIsLoading(true);
    const prompt = `Please translate all text in this image to ${
      LANGUAGES[targetLanguage as keyof typeof LANGUAGES]
    }. Format the output as a clear list of translations, with the original text followed by "→" and then the translation. If there are multiple separate text elements, list them separately.`;

    try {
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  inlineData: {
                    mimeType: 'image/jpeg',
                    data: imageData.split(',')[1], // Remove the data URL prefix
                  },
                },
                {
                  text: prompt,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(t('screentranslator.messages.apiError'));
      }

      const data = await response.json();
      if (data.candidates && data.candidates[0]) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.candidates[0].content.parts[0].text,
        };
        setMessages([assistantMessage]);
      }
    } catch (error) {
      setUploadState((prev) => ({
        ...prev,
        error: t('screentranslator.messages.apiError'),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const captureScreenshot = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor',
        },
      });

      // Create a video element to capture the stream
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      // Add a small delay to allow user to prepare
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Create a canvas to draw the video frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current frame to the canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }

      // Convert canvas to base64 image
      const base64Image = canvas.toDataURL('image/jpeg');
      setSelectedImage(base64Image);

      // Stop all tracks
      stream.getTracks().forEach((track) => track.stop());

      // Clean up
      video.srcObject = null;

      // Automatically trigger translation after screenshot
      handleTranslate(base64Image);
    } catch (error) {
      setUploadState((prev) => ({
        ...prev,
        error: t('screentranslator.messages.screenshotError'),
      }));
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3, fontFamily: 'Inter' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        >
          {messages.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<MessageSquare />}
              onClick={handleNewTranslation}
              sx={{
                boxShadow: 4,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'rgba(156, 39, 176, 0.04)',
                },
              }}
            >
              {t('screentranslator.buttons.newTranslation')}
            </Button>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="language-select-label">
                {t('screentranslator.labels.targetLanguage')}
              </InputLabel>
              <Select
                labelId="language-select-label"
                value={targetLanguage}
                label={t('screentranslator.labels.targetLanguage')}
                onChange={(e) => setTargetLanguage(e.target.value)}
                disabled={isLoading}
              >
                {Object.entries(LANGUAGES).map(([code, name]) => (
                  <MenuItem key={code} value={code}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={captureScreenshot}
                disabled={isLoading}
                startIcon={<Monitor />}
                sx={{
                  textTransform: 'none',
                  py: 1,
                  px: 3,
                }}
              >
                {t('screentranslator.buttons.takeScreenshot')}
              </Button>
            </Box>
          </Box>

          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt={t('screentranslator.alt.capturedImage')}
              sx={{
                width: '100%',
                height: 300,
                objectFit: 'contain',
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              fontFamily: 'Inter',
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 4,
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <CircularProgress />
                  <Typography color="text.secondary">
                    {t('screentranslator.messages.translating')}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              messages.map((message, index) => (
                <Box key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: 'Inter',
                        '& p': { m: 0 },
                        '& p:not(:last-child)': { mb: 1 },
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </Box>
                  </Paper>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Paper>

      <FeedbackSnackbar
        open={!!uploadState.error}
        message={uploadState.error || t('screentranslator.messages.error')}
        severity="error"
        onClose={() => setUploadState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default ScreenTranslator;
